import React, { FC } from 'react';
import { FiCheckCircle, FiXCircle } from 'react-icons/fi';
import { getColorForSubApplication } from '@/utils/subapp.util';

import {
  PillConfig,
  DEFAULT_PILL_CONFIG,
  ACTIVE_PILL_CONFIG,
  INACTIVE_PILL_CONFIG,
  STATUS_CODE_PILL_MAP,
  ALERT_STATUS_PILL_MAP,
  COOLER_STATUS_PILL_MAP,
  COOLER_TYPE_PILL_MAP,
  CRON_STATUS_PILL_MAP,
  ACTIVE_INACTIVE_PILL_MAP,
  VDI_SYNC_PILL_MAP,
  BOOL_ENABLED_PILL_MAP,
  STATUS_ENABLED_PILL_MAP,
  resolveStatusCodePill,
  getSubAppPillConfig,
  solidifyConfig,
} from './pillCell.maps';
import { PillCellProps } from './pillCell.types';

export type { PillCellProps, PillPreset, PillShape } from './pillCell.types';
export type { PillConfig } from './pillCell.maps';
import Tooltip from '@/components/atoms/Tooltip';

export {
  DEFAULT_PILL_CONFIG,
  ACTIVE_PILL_CONFIG,
  INACTIVE_PILL_CONFIG,
  STATUS_CODE_PILL_MAP,
  ALERT_STATUS_PILL_MAP,
  COOLER_STATUS_PILL_MAP,
  COOLER_TYPE_PILL_MAP,
  CRON_STATUS_PILL_MAP,
  ACTIVE_INACTIVE_PILL_MAP,
  VDI_SYNC_PILL_MAP,
  BOOL_ENABLED_PILL_MAP,
  STATUS_ENABLED_PILL_MAP,
  APP_LOG_STATUS_PILL_MAP,
  ACTION_LOG_STATUS_PILL_MAP,
  CONNECTION_LOG_EVENT_PILL_MAP,
  SMART_COOLER_DEVICE_STATUS_PILL_MAP,
  SMART_COOLER_SYSTEM_STATE_PILL_MAP,
  resolveStatusCodePill,
  getSubAppPillConfig,
} from './pillCell.maps';

// ─── Component ────────────────────────────────────────────────────────────────
const PillCell: FC<PillCellProps> = ({
  value,
  map,
  defaultConfig = DEFAULT_PILL_CONFIG,
  config,
  preset,
  dot = false,
  icon,
  label,
  shape = 'pill',
  className = '',
  size = 'text-[10px]',
  customStyle,
  testId,
  solid = false,
  tooltip,
  tooltipPlacement = 'top',
  tooltipTitle,
  tooltipValue,
}) => {
  const p = preset as string | undefined;
  const key = typeof value === 'string' ? value.toUpperCase() : String(value);

  // ── Resolve preset → config ───────────────────────────────────────────────
  const presetConfig: PillConfig | undefined =
    p === 'active'
      ? ACTIVE_PILL_CONFIG
      : p === 'inactive'
        ? INACTIVE_PILL_CONFIG
        : p === 'statusCode'
          ? resolveStatusCodePill(
            typeof value === 'number' ? value : Number(value),
          )
          : p === 'alertStatus'
            ? (ALERT_STATUS_PILL_MAP[key] ?? DEFAULT_PILL_CONFIG)
            : p === 'coolerStatus'
              ? (COOLER_STATUS_PILL_MAP[key] ?? DEFAULT_PILL_CONFIG)
              : p === 'coolerType'
                ? (COOLER_TYPE_PILL_MAP[key] ?? DEFAULT_PILL_CONFIG)
                : p === 'cronStatus'
                  ? (CRON_STATUS_PILL_MAP[key] ?? DEFAULT_PILL_CONFIG)
                  : p === 'vdiSync'
                    ? (VDI_SYNC_PILL_MAP[key] ?? DEFAULT_PILL_CONFIG)
                    : p === 'boolEnabled'
                      ? (BOOL_ENABLED_PILL_MAP[key] ?? DEFAULT_PILL_CONFIG)
                      : p === 'statusEnabled'
                        ? (STATUS_ENABLED_PILL_MAP[key] ?? DEFAULT_PILL_CONFIG)
                        : p === 'subapp' && typeof value === 'string'
                          ? getSubAppPillConfig(
                            getColorForSubApplication(value),
                          )
                          : undefined;

  // ── Resolve preset → icon ─────────────────────────────────────────────────
  const presetIcon: React.ReactNode =
    p === 'active' ? (
      <FiCheckCircle className="w-3 h-3" />
    ) : p === 'inactive' ? (
      <FiXCircle className="w-3 h-3" />
    ) : p === 'boolEnabled' && key === 'ENABLED' ? (
      <FiCheckCircle className="w-3 h-3" />
    ) : p === 'boolEnabled' && key === 'DISABLED' ? (
      <FiXCircle className="w-3 h-3" />
    ) : null;

  const resolvedIcon = icon ?? presetIcon;
  const upperKey = typeof value === 'string' ? value.toUpperCase() : value;
  const rawResolved: PillConfig =
    presetConfig ??
    (map && upperKey !== undefined ? map[upperKey] : undefined) ??
    (map && value !== undefined ? map[value] : undefined) ??
    config ??
    defaultConfig;
  const resolved: PillConfig = solid ? solidifyConfig(rawResolved) : rawResolved;

  const showDot = dot && !!resolved.dot;
  const displayLabel = label ?? (value !== undefined ? String(value) : '—');
const tooltipText =
  tooltip === true
    ? displayLabel
    : tooltip === false || tooltip == null
      ? null
      : tooltip;

  const shapeClass =
    shape === 'ring'
      ? `rounded-lg ring-1 ${resolved.border ?? ''} font-mono`
      : shape === 'badge'
        ? `rounded-md ${resolved.border ? `border ${resolved.border}` : ''}`
        : shape === 'bar'
          ? ''
          : `rounded-full ${resolved.border ? `border ${resolved.border}` : ''}`;

  // ── bar shape ─────────────────────────────────────────────────────────────
  if (shape === 'bar') {
  const content = (
      <div
        data-testid={testId ?? ''}
        className={[
          'inline-flex items-center rounded-lg overflow-hidden shadow-sm',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        style={customStyle}
      >
        <div
          className={[
            'w-[3px] self-stretch',
            resolved.bar ?? resolved.dot ?? 'bg-gray-400',
          ].join(' ')}
        />
        <span
          className={[
            'inline-flex items-center gap-1.5 pr-2.5 pl-2 py-1 font-bold tracking-wide',
            size,
            resolved.bg,
            resolved.text,
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {showDot && resolved.dot && (
            <span
              className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${resolved.dot}`}
            />
          )}
          {resolvedIcon}
          {displayLabel}
        </span>
      </div>
    );

  if (tooltipText || tooltipTitle) {
    return (
      <Tooltip
        content={tooltipText as React.ReactNode || undefined}
        title={tooltipTitle}
        value={tooltipValue ?? (tooltipText as string | undefined)}
        placement={tooltipPlacement}
      >
        {content}
      </Tooltip>
    );
  }

  return content;
  }

  // ── all other shapes ──────────────────────────────────────────────────────
  const content = (
    <span
      data-testid={testId ?? ''}
      style={customStyle}
      className={[
        'inline-flex items-center gap-1.5 px-2.5 py-1 font-bold',
        size,
        shapeClass,
        resolved.bg,
        resolved.text,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {showDot && resolved.dot && (
        <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
          {resolved.pulse && (
            <span
              className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${resolved.dot}`}
            />
          )}
          <span
            className={`relative inline-flex rounded-full h-1.5 w-1.5 ${resolved.dot}`}
          />
        </span>
      )}
      {resolvedIcon}
      {displayLabel}
    </span>
  );

if (tooltipText || tooltipTitle) {
  return (
    <Tooltip
      content={tooltipText as React.ReactNode || undefined}
      title={tooltipTitle}
      value={tooltipValue ?? (tooltipText as string | undefined)}
      placement={tooltipPlacement}
    >
      {content}
    </Tooltip>
  );
}

return content;
};

export default PillCell;
